import * as z from "@-/com.npmjs.zod";

/**
 * Identifies which side of a grapheme the user is drilling. Routes encode this
 * as the `[type]` path segment: `meaning` for the meaning card, or
 * `pronunciation-<pinyin>` to target a specific pronunciation card by its
 * tone-number pinyin (e.g. `pronunciation-xing2`).
 *
 * The pronunciation reading is keyed by its `pinyin` string (tone-number form,
 * single syllable — `xing1`, `gan4`) rather than a positional index. Pinyin is a
 * stable identity for a reading: it's the same value the actor
 * `user_grapheme_pronunciation_card` row, the content
 * `app_grapheme_pronunciation_card` row, and the morpheme slice `pinyinList`
 * entries all carry, so it round-trips across deck/content drift where a
 * recomputed positional index would shift and rate the wrong card.
 */
const PRONUNCIATION_PREFIX = "pronunciation-";
/**
 * Word cards drill a word's characters from its pinyin, in the context of a sentence. Encoded
 * as `word-sentence-<toneNumbersPinyin>` — multi-syllable for a multi-character word.
 */
const WORD_SENTENCE_PREFIX = "word-sentence-";
/**
 * Sentence-production drills: the user is shown a sentence's ENGLISH translation and produces the
 * whole Chinese sentence, one word at a time, with incremental hints. Two variants by input mode —
 * `sentence-glyphs-` types the characters, `sentence-speech-` speaks them.
 *
 * The pinyin they carry is the drilled WORD's (the card's identity, the same value
 * `word-sentence-` carries), not the sentence's. Which sentence is being drilled is carried
 * separately as a `contextSentence` param (the sentence text), because the sentence rotates per review while the card does
 * not.
 */
const SENTENCE_GLYPHS_PREFIX = "sentence-glyphs-";
const SENTENCE_SPEECH_PREFIX = "sentence-speech-";

/**
 * Every route-segment prefix, longest-first.
 *
 * INVARIANT: no prefix here may be a prefix of another. `decode` tests them in order, so if that
 * ever stops holding the longest must be tested first or the shorter one silently shadows it.
 * Asserted in the package's tests rather than left to this comment.
 */
export const DRILL_TYPE_PREFIXES = [
  SENTENCE_GLYPHS_PREFIX,
  SENTENCE_SPEECH_PREFIX,
  WORD_SENTENCE_PREFIX,
  PRONUNCIATION_PREFIX,
] as const;

const meaningDrillTypeSchema = z.object({ kind: z.literal("meaning") });

const pronunciationDrillTypeSchema = z.object({
  kind: z.literal("pronunciation"),
  pinyin: z.string().min(1),
});

const wordSentenceDrillTypeSchema = z.object({
  kind: z.literal("word-sentence"),
  pinyin: z.string().min(1),
});

const sentenceGlyphsDrillTypeSchema = z.object({
  kind: z.literal("sentence-glyphs"),
  pinyin: z.string().min(1),
});

const sentenceSpeechDrillTypeSchema = z.object({
  kind: z.literal("sentence-speech"),
  pinyin: z.string().min(1),
});

const drillTypeSchema = z.union([
  meaningDrillTypeSchema,
  pronunciationDrillTypeSchema,
  wordSentenceDrillTypeSchema,
  sentenceGlyphsDrillTypeSchema,
  sentenceSpeechDrillTypeSchema,
]);

export type DrillType = z.output<typeof drillTypeSchema>;

/**
 * The drill kinds whose card is a WORD card (`user_word_sentence_card`) rather than a grapheme one.
 *
 * Exported as one predicate because the drill page's suspense set and its navigator's prefetch list
 * must branch on EXACTLY the same condition — they pass an empty-string sentinel for non-word
 * kinds, and a divergence between the two is silent (the page still renders, it just suspends on
 * every navigation). Sharing this function is what makes them unable to drift.
 */
export function isWordDrillKind(kind: DrillType["kind"]): boolean {
  return kind === "word-sentence" || kind === "sentence-glyphs" || kind === "sentence-speech";
}

/**
 * The drill kinds that produce a whole sentence incrementally, rather than grading one answer.
 *
 * These behave differently enough that several call sites need to ask the question directly: they
 * own an in-page state machine, they never navigate to the back route on reveal, and they suppress
 * the background tap-to-reveal affordance.
 */
export function isSentenceProductionDrillKind(kind: DrillType["kind"]): boolean {
  return kind === "sentence-glyphs" || kind === "sentence-speech";
}

/** Needs to be a string, e.g. if it's from URLSearchParams */
export const drillTypeCodec = z.codec(z.string().default("meaning"), drillTypeSchema, {
  decode: (raw): DrillType => {
    if (raw === undefined || raw === "meaning" || raw === "") {
      return { kind: "meaning" };
    }
    // Checked before the pronunciation prefix: no prefix is a prefix of another today (asserted in
    // the tests via DRILL_TYPE_PREFIXES), but ordering by specificity keeps that from becoming a
    // silent mis-decode if one ever is.
    if (raw.startsWith(SENTENCE_GLYPHS_PREFIX)) {
      const pinyin = decodeURIComponent(raw.slice(SENTENCE_GLYPHS_PREFIX.length));
      if (pinyin.length > 0) {
        return { kind: "sentence-glyphs", pinyin };
      }
    }
    if (raw.startsWith(SENTENCE_SPEECH_PREFIX)) {
      const pinyin = decodeURIComponent(raw.slice(SENTENCE_SPEECH_PREFIX.length));
      if (pinyin.length > 0) {
        return { kind: "sentence-speech", pinyin };
      }
    }
    if (raw.startsWith(WORD_SENTENCE_PREFIX)) {
      // Word pinyin is multi-syllable (e.g. `shui4jiao4`) but still ASCII.
      const pinyin = decodeURIComponent(raw.slice(WORD_SENTENCE_PREFIX.length));
      if (pinyin.length > 0) {
        return { kind: "word-sentence", pinyin };
      }
    }
    if (raw.startsWith(PRONUNCIATION_PREFIX)) {
      // Tone-number pinyin is single-syllable and ASCII (letters + a tone digit),
      // so it needs no URL escaping, but decode defensively in case a caller
      // percent-encoded the segment.
      const pinyin = decodeURIComponent(raw.slice(PRONUNCIATION_PREFIX.length));
      if (pinyin.length > 0) {
        return { kind: "pronunciation", pinyin };
      }
    }
    return { kind: "meaning" };
  },
  encode: (drillType) => {
    switch (drillType.kind) {
      case "meaning": {
        return "meaning";
      }
      case "pronunciation": {
        return `${PRONUNCIATION_PREFIX}${drillType.pinyin}`;
      }
      case "word-sentence": {
        return `${WORD_SENTENCE_PREFIX}${drillType.pinyin}`;
      }
      case "sentence-glyphs": {
        return `${SENTENCE_GLYPHS_PREFIX}${drillType.pinyin}`;
      }
      case "sentence-speech": {
        return `${SENTENCE_SPEECH_PREFIX}${drillType.pinyin}`;
      }
    }
  },
});
